import * as yup from "yup";
import phoneNumberValidator from "../../utils/phoneNumberValidator";
import emailValidator from "../../utils/emailValidator";
import { validRakutenDomains } from "../../utils/utils";
import { isDate } from "date-fns";
import {
  handleUnitChange,
  handleUnitDistributionList,
  handleUnitShortUrl,
} from "../../utils/utils";
import { adminRoles } from "../../constants/AdminRoles";
import {
  differenceInMillisecondsUtil,
  formatDateUtil,
  getCurrentDateWithTimezone,
  isValidDate,
  parseDate,
} from "../../utils/dateFnsUtils";

const validateIpAddress = (ipAddress) => {
  //TODO: change this to validate CIDR block properly and IPV6
  if (ipAddress.includes("/")) {
    const [address, prefix] = ipAddress.split("/");
    if (prefix.length === 0 || Number(prefix) < 0 || Number(prefix) > 32) {
      return false;
    }
    return validateIpAddress(address);
  }

  const octets = ipAddress.split(".");
  if (octets.length !== 4) {
    return false;
  }
  for (const octet of octets) {
    if (
      isNaN(octet) ||
      octet.length === 0 ||
      octet.length > 3 ||
      Number(octet) < 0 ||
      Number(octet) > 255
    ) {
      return false;
    }
  }
  return true;
};

yup.addMethod(yup.string, "phoneNumber", phoneNumberValidator);
yup.addMethod(yup.string, "integer", function () {
  return this.matches(/^\d+$/, "should_be_integer");
});
yup.addMethod(yup.string, "email", emailValidator);

export const resellerValidationSchema = () => {
  return yup.object({
    name: yup.string("reseller_name_error").required("reseller_name_required"),
    reseller_type: yup
      .string("reseller_type_error")
      .required("reseller_type_required"),
  });
};

export const adminUserValidationSchema = () => {
  return yup.object({
    first_names: yup.string().required("first_name_required"),
    last_name: yup.string().required("last_name_required"),
    email: yup.string().email("valid_email").required("email_required"),
    admin_type: yup.string(),
    reseller_id: yup.string().when("admin_type", {
      is: adminRoles.reseller,
      then: (schema) => schema.required("reseller_name_required"),
    }),
    phone_number: yup
      .string()
      .required("phone_number_required")
      .phoneNumber("invalid_phone"),
    user_mfa_profile: yup
      .mixed()
      .oneOf(["NONE", "GOOGLE_AUTHENTICATOR", "OKTA"])
      .required("user_mfa_required"),
  });
};

export const userValidationSchema = () => {
  return yup.object({
    first_names: yup.string().required("first_name_required"),
    last_name: yup.string(),
    email: yup
      .string()
      .email("valid_email")
      .required("email_required")
      .when("user_mfa_profile", {
        is: "OKTA",
        then: (schema) =>
          schema.test("rakuten_email", "rakuten_email_required", (value) => {
            const domain = value.split("@")[1];
            return domain && validRakutenDomains.includes(domain.toLowerCase());
          }),
        otherwise: (schema) =>
          schema.test("non_rakuten_email", "non_rakuten_email_required", (value) => {
            const domain = value.split("@")[1];
            return domain && !validRakutenDomains.includes(domain.toLowerCase());
          }),
      }),
    name_title: yup.string(),
    phone_number: yup
      .string()
      .required("phone_number_required")
      .phoneNumber("invalid_phone"),
    user_mfa_profile: yup
      .mixed()
      .oneOf(["NONE", "GOOGLE_AUTHENTICATOR", "OKTA"])
      .required("user_mfa_required"),
  });
};

export const whitelistSenderAddressValidationSchema = () => {
  return yup.object({
    sender_address: yup
      .string()
      .test("type", (value, { createError, path }) => {
        if (isNaN(value) && value.length > 11)
          return createError({ path, message: "sender_id_alphanumeric_limit" });
        // Check for '=' and '%'
        if (value.includes("=") || value.includes("%")) {
          return createError({
            path,
            message: "sender_id_special_char_not_allowed",
          });
        } else if (!isNaN(value) && value.length > 15)
          return createError({ path, message: "sender_id_numerical_limit" });
        else return true;
      })
      .required("sender_address_required"),
    country_code: yup.string().required("country_code_required"),
  });
};

export const whitelistSenderPendingRequestValidationSchema = () => {
  return yup.object({
    enterprise_id: yup.string().required("enterprise_id_required"),
    sender_address: yup.string().required("sender_address_required"),
    country_code: yup.string().required("country_code_required"),
  });
};

export const searchLookupSchema = () => {
  return yup.object({
    searchType: yup.string().required("search_type_required"),
    message_id: yup.string().when("searchType", {
      is: "message_id",
      then: (schema) => schema.required("message_id_required"),
    }),
    enterpriseId: yup.string().when("searchType", {
      is: "api_key",
      then: (schema) => schema.required("enterprise_id_required"),
    }),
    apiKey: yup.string().when("searchType", {
      is: "api_key",
      then: (schema) => schema.required("api_key_required"),
    }),
    dateFrom: yup.string().required("date_range_required"),
    dateTo: yup.string().required("date_range_required"),
  });
};

export const signupValidationSchema = () => {
  return yup.object({
    first_names: yup.string("first_name_error").required("first_name_required"),
    last_name: yup.string("last_name_error").required("last_name_required"),
    email: yup.string("email").email("valid_email").required("email_required"),
    title: yup.string("title_error"),
    phone_number: yup
      .string()
      .required("phone_number_required")
      .phoneNumber("invalid_phone"),
  });
};

export const bankAccountValidationSchema = () => {
  const AlphaNumericMatch = "[A-Za-z0-9_]";
  return yup.object({
    name: yup.string("name_error").required("name_required"),
    description: yup.string(),
    account_number: yup
      .number()
      .required("account_number_required")
      .typeError("account_number_number"),
    bic_code: yup
      .string()
      .required("bic_code_required")
      .matches(AlphaNumericMatch, "Alphanumeric bic code required"),
    swift_code: yup
      .string()
      .required("swift_code_required")
      .matches(AlphaNumericMatch, "Alphanumeric swift code required"),
    sort_code: yup
      .string()
      .required("sort_code_required")
      .matches(AlphaNumericMatch, "Alphanumeric sort code required"),
    iban_number: yup
      .string()
      .required("iban_number_required")
      .matches(AlphaNumericMatch, "Alphanumeric iban code required"),
    enterprise_id: yup.string().required("enterprise id required"),
  });
};

const checkFutureSelectedTime = (value, timezone) => {
  if (value === "send_immediately") {
    return true;
  }

  if (!isValidDate(value)) {
    return false;
  }

  // Get current time adjusted for timezone
  const currentDateTime = getCurrentDateWithTimezone(
    timezone === "JST" ? "JST" : "UTC",
  );

  // Parse the selected time using your utility method
  const selectedDateTime = parseDate(value);

  // Compare directly - selected time should be after current time
  return differenceInMillisecondsUtil(selectedDateTime, currentDateTime) > 0;
};

export const campaignValidationSchema = (
  isTemplateRequired,
  messageLength,
  maxMessageLength,
  timezone,
) => {
  // console.log('apiKeys', apiKeys);
  const schema = {
    campaign_name: yup
      .string()
      .required("campaign_name_required")
      .max(100, "campaign_name_character_exceeded"),
    enterprise_id: yup.string().required("enterprise_id_required"),
    entity_id: yup.string(),
    from: yup.string().required("from_required"),
    user_id: yup.string().required("user_id_required"),
    scheduled_send_timestamp: yup
      .string()
      .test("format", "schedule_time_pas_time_validation", (value) => {
        return checkFutureSelectedTime(value, timezone);
      })
      .required("schedule_message_required"),
    api_key: yup.string().required("api_key_required"),
    distribution_list_id: yup.string().required("distribution_list_name_required"),
  };

  if (isTemplateRequired) {
    schema.message = yup
      .string()
      .required("message_required")
      .test("test-max-length", "exceed_message_length", function (value) {
        return !(messageLength > maxMessageLength);
      });
  }
  return yup.object(schema);
};

export const campaignValidationSchemaAdmin = (
  isTemplateRequired,
  apiKeys,
  messageLength,
  maxMessageLength,
  timezone,
) => {
  const schema = {
    campaign_name: yup
      .string()
      .required("campaign_name_required")
      .max(100, "campaign_name_character_exceeded"),
    enterpriseId: yup.string().required("enterprise_id_required"),
    entityId: yup.string(),
    from: yup
      .string()
      .test("type", (value, { createError, path }) => {
        if (isNaN(value) && value.length > 11)
          return createError({ path, message: "sender_id_alphanumeric_limit" });
        else if (!isNaN(value) && value.length > 15)
          return createError({ path, message: "sender_id_numerical_limit" });
        else return true;
      })
      .required("sender_required"),
    user_id: yup.string().required("user_id_required"),
    scheduled_send_timestamp: yup
      .string()
      .test("format", "schedule_time_pas_time_validation", (value) => {
        return checkFutureSelectedTime(value, timezone);
      })
      .required("schedule_message_required"),
    apiKey: yup
      .string()
      .required("api_key_required")
      .test("test-disabled-options", "api_key_disabled", function (value) {
        const selectedValue = apiKeys?.find(
          (eachKey) => eachKey.account_id === value,
        );
        return (
          selectedValue?.account_status === "ENABLED" ||
          selectedValue?.account_status === "DUMMY_ACCOUNT"
        );
      }),
    distribution_list_id: yup.string().required("distribution_list_name_required"),
    click_thru_link_tracking_remote_url: yup
      .string()
      .when("click_thru_link_tracking_required", {
        is: true,
        then: (schema) => schema
          .required("click_thru_link_tracking_remote_url_required")
          .min(1, "click_thru_link_tracking_remote_url_required")
          .url("click_thru_link_tracking_remote_url_invalid"),
        otherwise: (schema) => schema.nullable()
      }),
  };

  if (isTemplateRequired) {
    schema.text = yup
      .string()
      .required("message_required")
      .test("test-max-length", "exceed_message_length", function (value) {
        return !(messageLength > maxMessageLength);
      })
      .when("click_thru_link_tracking_required", {
        is: true,
        then: (schema) => schema.test(
          "contains-click-thru-link",
          "text_must_contain_click_thru_link_placeholder",
          function (value) {
            return value && value.includes("${click-thru-link}");
          }
        ),
        otherwise: (schema) => schema
        });
    // schema.template_id = yup.string().required("sort_code_required");
  }
  return yup.object(schema);
};

export const campaignValidationSchemaUser = (
  isTemplateRequired,
  apiKeys,
  messageLength,
  maxMessageLength,
  timezone,
) => {
  const schema = {
    campaign_name: yup
      .string()
      .required("campaign_name_required")
      .max(100, "campaign_name_character_exceeded"),
    enterprise_id: yup.string().required("enterprise_id_required"),
    entity_id: yup.string(),
    from: yup.string().required("from_required"),
    user_id: yup.string().required("user_id_required"),
    scheduled_send_timestamp: yup
      .string()
      .test("format", "schedule_time_pas_time_validation", (value) => {
        return checkFutureSelectedTime(value, timezone);
      }),
    api_key: yup
      .string()
      .required("api_key_required")
      .test("test-disabled-options", "api_key_disabled", function (value) {
        const selectedValue = apiKeys?.find(
          (eachKey) => eachKey.account_id === value,
        );
        return (
          selectedValue?.account_status === "ENABLED" ||
          selectedValue?.account_status === "DUMMY_ACCOUNT"
        );
      }),
    distribution_list_id: yup.string().required("distribution_list_name_required"),
  };

  if (isTemplateRequired) {
    schema.message = yup
      .string()
      .required("message_required")
      .test("test-max-length", "exceed_message_length", function (value) {
        return !(messageLength > maxMessageLength);
      });
    // schema.template_id = yup.string().required("sort_code_required");
  }
  return yup.object(schema);
};

export const unifiedCampaignValidationSchemaUser = (
  isTemplateRequired,
  apiKeys,
  messageLength,
  maxMessageLength,
  timezone,
) => {
  const schema = {
    campaign_name: yup
      .string()
      .required("campaign_name_required")
      .max(100, "campaign_name_character_exceeded"),
    enterpriseId: yup.string().required("enterprise_id_required"),
    entityId: yup.string(),
    from: yup.string().required("from_required"),
    user_id: yup.string().required("user_id_required"),
    scheduled_send_timestamp: yup
      .string()
      .test("format", "schedule_time_pas_time_validation", (value) => {
        return checkFutureSelectedTime(value, timezone);
      }),
    distribution_list_id: yup.string().required("distribution_list_name_required"),
    click_thru_link_tracking_remote_url: yup
    .string()
    .when("click_thru_link_tracking_required", {
      is: true,
      then: (schema) => schema
        .required("click_thru_link_tracking_remote_url_required")
        .min(1, "click_thru_link_tracking_remote_url_required")
        .url("click_thru_link_tracking_remote_url_invalid"),
      otherwise: (schema) => schema.nullable()
    }),
  };

  if (isTemplateRequired) {
    schema.text = yup
      .string()
      .required("message_required")
      .test("test-max-length", "exceed_message_length", function (value) {
        return !(messageLength > maxMessageLength);
      })
      .when("click_thru_link_tracking_required", {
        is: true,
        then: (schema) => schema.test(
          "contains-click-thru-link",
          "text_must_contain_click_thru_link_placeholder",
          function (value) {
            return value && value.includes("${click-thru-link}");
          }
        ),
        otherwise: (schema) => schema
        });
    // schema.template_id = yup.string().required("sort_code_required");
  }
  return yup.object(schema);
};

export const templateFormValidation = (messageMetaData) =>
  yup.object({
    name: yup.string().required("template_name_required"),
    description: yup.string().required("description_required"),
    message_contents: yup
      .string()
      .required("message_required")
      .when("unicode", {
        is: "Yes",
        then: (schema) =>
          schema.test("unicode-radio", "non_unicode_content", (value) => {
            return messageMetaData?.auto_detected_unicode === undefined
              ? true
              : messageMetaData && messageMetaData?.auto_detected_unicode;
          }),
        otherwise: (schema) =>
          schema.test("unicode-radio", "unicode_content", (value) => {
            return messageMetaData?.auto_detected_unicode === undefined
              ? true
              : messageMetaData && !messageMetaData?.auto_detected_unicode;
          }),
      }),
  });

export const contactValidationSchema = () => {
  return yup.object({
    contact_type: yup.string(),
    role: yup.string().required("role_required"),
    department: yup.string().required("department_required"),
    location: yup.string(),
    country_code: yup.string(),
    time_zone: yup.string(),
    mobile_phone_number: yup
      .string()
      .required("mobile_number_required")
      .phoneNumber("invalid_mobile"),
    contact_phone_number: yup
      .string()
      .required("phone_number_required")
      .phoneNumber("invalid_phone"),
    first_names: yup.string().required("first_name_required"),
    last_name: yup.string().required("last_name_required"),
    email: yup.string().email("valid_email").required("email_required"),
    name_title: yup.string(),
  });
};

export const billingAccountValidationSchema = () => {
  return yup.object({
    tax_region: yup.string(),
    currency_code: yup.string(),
    credit_limit: yup.number(),
    name: yup.string().required("billing_account_name_required"),
  });
};

export const inputMessageBoxValidationSchema = (values) => {
  const validationObj = {
    subject: yup.string().required("subject_required"),
    payload: yup.string().required("description_required"),
    enterprise_id: yup.string().required("enterprise_id_required"),
  };

  if (values?.system_message_audience === "entity") {
    validationObj.entity_id = yup.string().required("entity_id_required");
  }

  if (values?.system_message_audience === "enterprise_users") {
    validationObj.user_id = yup.string().required("user_id_required");
  }

  return yup.object(validationObj);
};

export const addressValidationSchema = () => {
  return yup.object({
    label: yup.string(),
    street_address_1: yup.string().required("street_address_required"),
    street_address_2: yup.string(),
    city: yup.string().required("city_required"),
    region: yup.string().required("region_required"),
    zip: yup.string().required("zip_required"),
    country_code: yup.string(),
  });
};

export const allowedSourceIpValidationSchema = () => {
  return yup.object({
    allowed_source_ip: yup
      .string()
      .test("valid-ip-address", "invalid_ip_address", validateIpAddress)
      .required("allowed_source_ip_required"),
  });
};

export const distributionListValidationSchema = () => {
  return yup.object({
    name: yup.string().required("distribution_list_name_required"),
    description: yup.string(),
  });
};

export const distributionListEntryValidationSchema = () => {
  return yup.object({
    name_title: yup.string(),
    email_address: yup.string(),
    first_names: yup.string(),
    last_name: yup.string(),
    phone_number: yup.string().phoneNumber("invalid_phone"),
  });
};

export const enterpriseListValidationSchema = yup.object({
  name: yup.string("name").required("name_required"),
  display_name: yup.string(),
  enterprise_mfa_profile: yup
    .string("enterprise_mfa_profile")
    .required("enterprise_mfa_profile_required"),
  set_as_trial_account: yup.boolean(),
  trial_limit: yup
    .string()
    .integer("invalid_value")
    .when("set_as_trial_account", {
      is: true,
      then: (schema) =>
        schema
          .required("trial_limit_required")
          .test("greater than 100000", "invalid_trial_limit", (value) => {
            return parseInt(value) <= 100000;
          }),
    }),
});

export const apiKeyValidationSchema = (filterformSelectData) =>
  yup.object({
    billing_account_id: yup
      .string()
      .required("billing_account_required")
      .test("billing_account_valid", "billing_account_mandatory", function (value) {
        const accounts = filterformSelectData?.billingAccountsList || [];
        if (!value) {
          return false;
        }
        if (accounts.length === 0) {
          return false;
        }
        return accounts.some((acc) => acc.billing_account_id === value);
      }),
    name: yup.string("name").required("name_required"),
    max_callback_queue_size: yup.string().integer("invalid_value"),
    max_mt_per_second: yup
      .string()
      .integer("invalid_value")
      .test("isZero", "max_mt_per_second_cannot_be_zero", (value) => {
        return !(value == 0);
      }),
    max_mo_per_second: yup
      .string()
      .integer("invalid_value")
      .test("isZero", "max_mo_per_second_cannot_be_zero", (value) => {
        return !(value == 0);
      }),
    smpp_how_many_binds_allowed: yup
      .string()
      .integer("invalid_value")
      .test(
        "isZero",
        "smpp_how_many_binds_allowed_cannot_be_zero",
        (value, context) => {
          return !(value == 0 && context.parent.api_channel === "SMPP");
        },
      )
      .test(
        "should be <=100",
        "smpp_how_many_binds_allowed_limit_crossed",
        (value) => {
          return !(value > 100);
        },
      ),
    can_not_send_traffic_before_date: yup.date(),
    max_limit: yup
      .string()
      .integer("invalid_value")
      .test("greater than 100000", "invalid_trial_limit", (value) => {
        if (!value) return true;
        return parseInt(value) <= 100000;
      }),
  });

export const convertToSmppValidationSchema = yup.object({
  back_end_smpp_system_id: yup.string().required("smpp_backend_system_id_required"),
  smpp_how_many_binds_allowed: yup
    .string()
    .integer("invalid_value")
    .test("isZero", "smpp_how_many_binds_allowed_cannot_be_zero", (value) => {
      return !(value == 0);
    })
    .test(
      "should be <=100",
      "smpp_how_many_binds_allowed_limit_crossed",
      (value) => {
        return !(value > 100);
      },
    )
    .required("smpp_how_many_binds_allowed_required"),
});

export const urlValidationSchema = () => {
  function parseDateString(value, originalValue) {
    return isDate(originalValue) ? originalValue : new Date(originalValue);
  }

  return yup.object({
    name: yup.string().required("url_name_required"),
    enterprise_id: yup.string().required("enterprise_id_required"),
    max_times_allowed_to_access: yup.string(),
    //.required("max_times_allowed_to_access_required"),
    valid_until_date: yup
      .date()
      .transform(parseDateString)
      .required("valid_until_date_required")
      .min(new Date().setHours(0, 0, 0, 0), "valid_until_date_past"),
    description: yup.string(),
    entity_id: yup.string(),
    remote_url: yup.string().required("remote_url_required"),
  });
};

export const campaignOptOutSchema = yup.object({
  phone: yup.string().phoneNumber("invalid_phone"),
});

export const editInvoiceSchema = yup.object({
  line_item_type: yup.string().required("adjustment_type_required"),
  price: yup.number().required("price_required").typeError("price_number"),
  description: yup.string().required("description_required"),
});

export const apiKeyCredentailsValidationSchema = () => {
  return yup.object({
    valid_until: yup.date().nullable(true),
  });
};

const baseLimitsValidationSchema = yup
  .number()
  .typeError("should_be_integer") //Custom type error message for non-numeric input
  .test("doesNotStartWithSign", "should_be_integer", (value) => {
    return !/^[+-]/.test(value); // Check if value does not start with + or - but yup converting +10 as 10
  })
  .test("isZero", "cannot_be_below_min_limit", (validity) => {
    return !(validity === 0);
  })
  .test("isInteger", "should_be_integer", (value) => {
    // Check if there is a value in the input field
    if (value !== undefined && value !== "") {
      // Only validate if there is a non-empty value
      return Number.isInteger(value);
    }
    return true;
  });

export const EnterpriseCustomLimitsSchema = (maxValue) => {
  return yup.object({
    max_allowed_entities_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_entities_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_entities_per_enterprise);
      },
    ),
    max_allowed_addresses_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_addresses_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_addresses_per_enterprise);
      },
    ),
    max_allowed_contacts_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_contacts_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_contacts_per_enterprise);
      },
    ),
    max_allowed_bilateral_relationships_per_enterprise:
      baseLimitsValidationSchema.test(
        "max_allowed_bilateral_relationships_per_enterprise",
        "cannot_exceed_max_limit",
        (value) => {
          return !(
            value > maxValue?.max_allowed_bilateral_relationships_per_enterprise
          );
        },
      ),
    max_allowed_users_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_users_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_users_per_enterprise);
      },
    ),
    max_allowed_api_keys_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_api_keys_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_api_keys_per_enterprise);
      },
    ),
    max_allowed_credentials_per_api_key: baseLimitsValidationSchema
      .test(
        "max_allowed_credentials_per_api_key",
        "cannot_exceed_max_limit",
        (value) => {
          return !(value > maxValue?.max_allowed_credentials_per_api_key);
        },
      )
      .test("cannot_be_less_than", "cannot_be_below_min_limit", (value) => {
        return !(value < 3);
      }),
    max_allowed_meta_items_per_api_key: baseLimitsValidationSchema.test(
      "max_allowed_meta_items_per_api_key",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_meta_items_per_api_key);
      },
    ),
    max_allowed_billing_accounts_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_billing_accounts_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_billing_accounts_per_enterprise);
      },
    ),
    max_allowed_bank_accounts_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_bank_accounts_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_bank_accounts_per_enterprise);
      },
    ),
    max_allowed_whitelisted_sender_addresses_per_enterprise:
      baseLimitsValidationSchema.test(
        "max_allowed_whitelisted_sender_addresses_per_enterprise",
        "cannot_exceed_max_limit",
        (value) => {
          return !(
            value > maxValue?.max_allowed_whitelisted_sender_addresses_per_enterprise
          );
        },
      ),
    default_validity_period: baseLimitsValidationSchema.when(
      "validity_period_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema.max(handleUnitChange(unit.toString()), "cannot_exceed_max_limit")
          : schema;
      },
    ),
    fixed_validity_period: baseLimitsValidationSchema.when(
      "validity_period_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema.max(handleUnitChange(unit.toString()), "cannot_exceed_max_limit")
          : schema;
      },
    ),
    max_validity_period: baseLimitsValidationSchema.when(
      "validity_period_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema.max(handleUnitChange(unit.toString()), "cannot_exceed_max_limit")
          : schema;
      },
    ),
    max_allowed_users_per_entity: baseLimitsValidationSchema.test(
      "max_allowed_users_per_entity",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_users_per_entity);
      },
    ),
  });
};

export const DistributionListLimitsSchema = (maxValue) => {
  return yup.object({
    max_allowed_distribution_lists_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_bank_accounts_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_distribution_lists_per_enterprise);
      },
    ),
    max_allowed_entries_per_distribution_list: baseLimitsValidationSchema.test(
      "max_allowed_bank_accounts_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_entries_per_distribution_list);
      },
    ),
    max_allowed_entries_across_all_distribution_lists:
      baseLimitsValidationSchema.test(
        "max_allowed_entries_across_all_distribution_lists",
        "cannot_exceed_max_limit",
        (value) => {
          return !(
            value > maxValue?.max_allowed_entries_across_all_distribution_lists
          );
        },
      ),
    max_allowed_templates_per_enterprise: baseLimitsValidationSchema.test(
      "max_allowed_templates_per_enterprise",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_templates_per_enterprise);
      },
    ),
    max_bulk_messages_allowed_to_send_per_month: baseLimitsValidationSchema.test(
      "max_bulk_messages_allowed_to_send_per_month",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_bulk_messages_allowed_to_send_per_month);
      },
    ),
    max_allowed_concurrently_running_batches_per_enterprise:
      baseLimitsValidationSchema.test(
        "max_allowed_concurrently_running_batches_per_enterprise",
        "cannot_exceed_max_limit",
        (value) => {
          return !(
            value > maxValue?.max_allowed_concurrently_running_batches_per_enterprise
          );
        },
      ),
    max_ttl_for_distribution_lists_count: baseLimitsValidationSchema.when(
      "max_ttl_for_distribution_lists_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema.max(
              handleUnitDistributionList(unit.toString()),
              "cannot_exceed_max_limit",
            )
          : schema;
      },
    ),
    default_ttl_for_distribution_lists_count: baseLimitsValidationSchema.when(
      "default_ttl_for_distribution_lists_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema.max(
              handleUnitDistributionList(unit.toString()),
              "cannot_exceed_max_limit",
            )
          : schema;
      },
    ),
    fixed_ttl_for_distribution_lists_count: baseLimitsValidationSchema.when(
      "fixed_ttl_for_distribution_lists_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema.max(
              handleUnitDistributionList(unit.toString()),
              "cannot_exceed_max_limit",
            )
          : schema;
      },
    ),
  });
};

export const ShortUrlLimitsSchema = (maxValue) => {
  return yup.object({
    max_allowed_active_short_urls: baseLimitsValidationSchema.test(
      "max_allowed_active_short_urls",
      "cannot_exceed_max_limit",
      (value) => {
        return !(value > maxValue?.max_allowed_active_short_urls);
      },
    ),
    default_ttl_for_short_urls_count: baseLimitsValidationSchema.when(
      "default_ttl_for_short_urls_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema
              .max(
                handleUnitShortUrl(unit.toString()).maxTTL,
                "cannot_exceed_max_limit",
              )
              .min(
                handleUnitShortUrl(unit.toString()).minTTL,
                "cannot_be_below_min_limit",
              )
          : schema;
      },
    ),
    max_ttl_for_short_urls_count: baseLimitsValidationSchema.when(
      "max_ttl_for_short_urls_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema
              .max(
                handleUnitShortUrl(unit.toString()).maxTTL,
                "cannot_exceed_max_limit",
              )
              .min(
                handleUnitShortUrl(unit.toString()).minTTL,
                "cannot_be_below_min_limit",
              )
          : schema;
      },
    ),
    fixed_ttl_for_short_urls_count: baseLimitsValidationSchema.when(
      "fixed_ttl_for_short_urls_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema
              .max(
                handleUnitShortUrl(unit.toString()).maxTTL,
                "cannot_exceed_max_limit",
              )
              .min(
                handleUnitShortUrl(unit.toString()).minTTL,
                "cannot_be_below_min_limit",
              )
          : schema;
      },
    ),
  });
};

export const OptOutLimitsSchema = () => {
  return yup.object({
    opt_out_link_custom_ttl_count: baseLimitsValidationSchema.when(
      "opt_out_link_custom_ttl_unit",
      (unit, schema) => {
        return unit.toString()
          ? schema
              .max(
                handleUnitShortUrl(unit.toString()).maxTTL,
                "cannot_exceed_max_limit",
              )
              .min(
                handleUnitShortUrl(unit.toString()).minTTL,
                "cannot_be_below_min_limit",
              )
          : schema;
      },
    ),
    opt_out_link_per_phonenumber_url_enabled: yup.boolean(),
  });
};

export const enterpriseDetailsSchema = () => {
  return yup.object({
    name: yup.string("name").required("name_required"),
    enterprise_mfa_profile: yup
      .string("enterprise_mfa_profile")
      .required("enterprise_mfa_profile_required"),
    set_as_trial_account: yup.string().required("set_as_trial_account_required"),
    trial_limit: yup
      .string()
      .integer("invalid_value")
      .when("set_as_trial_account", {
        is: "yes",
        then: (schema) =>
          schema
            .required("trial_limit_required")
            .test("greater than 100000", "invalid_trial_limit", (value) => {
              return parseInt(value) <= 100000;
            }),
      }),
  });
};

export const entityDetailsSchema = () => {
  return yup.object({
    name: yup.string("name").required("name_required"),
    enable_monthly_max_limit: yup.string(),
    monthly_max_limit: yup
      .string()
      .integer("invalid_value")
      .when("enable_monthly_max_limit", {
        is: "yes",
        then: (schema) =>
          schema
            .required("trial_limit_required")
            .test("greater than 100000", "invalid_trial_limit", (value) => {
              return parseInt(value) <= 100000;
            }),
      }),
  });
};

export const enterpriseTrialLimitSchema = () => {
  return yup.object({
    trial_limit: yup
      .string()
      .integer("invalid_value")
      .required("trial_limit_required")
      .test("greater than 100000", "invalid_trial_limit", (value) => {
        return parseInt(value) <= 100000;
      }),
  });
};

export const entityEditSchema = () => {
  return yup.object({
    name: yup.string("name").required("name_required"),
    enable_monthly_max_limit: yup.string(),
    monthly_max_limit: yup
      .string()
      .integer("invalid_value")
      .when("enable_monthly_max_limit", {
        is: "yes",
        then: (schema) =>
          schema
            .required("trial_limit_required")
            .test("greater than 100000", "invalid_trial_limit", (value) => {
              return parseInt(value) <= 100000;
            }),
      }),
  });
};

const senderAddressValidation = yup
  .string()
  .required("sender_address_required")
  .test(
    "test-max-length",
    "exceed_message_length",
    function (value, { createError, path }) {
      if (!value) return false; // Required check
      if (value.length > 255) {
        return false;
      }
      const { type } = this.parent;
      if (type === "PLAIN" || type === "PREFIX") {
        if (isNaN(value) && value.length > 11)
          return createError({ path, message: "sender_id_alphanumeric_limit" });
        else if (!isNaN(value) && value.length > 15)
          return createError({ path, message: "sender_id_numerical_limit" });
        else return true;
      }
      return true;
    },
  );

export const blackListSenderIdSchema = () => {
  return yup.object({
    product: yup.string(),
    country_code: yup.string(),
    type: yup.string(),
    sender_address: senderAddressValidation,
  });
};

const valueValidation = yup
  .string()
  .required("value_is_required")
  .test(
    "test-max-length",
    "exceed_message_length",
    function (value, { createError, path }) {
      if (!value) return false; // Required check
      if (value.length > 255) {
        return false;
      }
      const { type } = this.parent;
      const isIlleggalSenderWordOrSenderSubstring =
        type === "ILLEGAL_SENDER_WORD" || type === "ILLEGAL_SENDER_SUBSTRING";
      if (isIlleggalSenderWordOrSenderSubstring) {
        if (isNaN(value) && value.length > 11)
          return createError({ path, message: "sender_id_alphanumeric_limit" });
        else if (!isNaN(value) && value.length > 15)
          return createError({ path, message: "sender_id_numerical_limit" });
        else return true;
      }
      return true;
    },
  );

export const spamFilterRuleSchema = () => {
  return yup.object({
    value: valueValidation,
  });
};

export const appListValidationSchema = () => {
  return yup.object({
    name: yup
      .string()
      .required("name_required")
      .max(200, "app_name_character_exceeded"),
    description: yup.string().max(200, "description_character_exceeded"),
    channel_secret: yup.string().required("channel_secret_required"),
    channel_token: yup.string().required("channel_token_required"),
    notes: yup.string().max(1024, "note_character_exceeded"),
  });
};
